import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { RiserConfig, RiserDef } from './riserConfig';
import { defaultRisers, cloneRisers, riserItems, riserSummary } from './riserConfig';
import RiserGraphic from './RiserGraphic';
import { riserViewBox } from './riserConfig';

export interface RiserEditorProps {
  config: RiserConfig | null;
  color: string;
  onConfigChange: (cfg: RiserConfig) => void;
  onClose: () => void;
}

function RiserRow({ def, onChange }: { def: RiserDef; onChange: (d: RiserDef) => void }) {
  const { t } = useTranslation();
  // W/D stored in cm; exposed in meters for a stage-scale editor.
  return (
    <div className="dk-row">
      <div className="form-group">
        <label>{t('stagePlotEditor.widthM')}</label>
        <input type="number" min={0.2} max={6} step={0.1} value={Math.round((def.width / 100) * 10) / 10} onChange={(e) => onChange({ ...def, width: Math.max(20, (Number(e.target.value) || 0.2) * 100) })} />
      </div>
      <div className="form-group">
        <label>{t('stagePlotEditor.depthM')}</label>
        <input type="number" min={0.2} max={6} step={0.1} value={Math.round((def.depth / 100) * 10) / 10} onChange={(e) => onChange({ ...def, depth: Math.max(20, (Number(e.target.value) || 0.2) * 100) })} />
      </div>
      <div className="form-group">
        <label>{t('stagePlotEditor.heightCm')}</label>
        <input type="number" min={5} max={200} value={def.height} onChange={(e) => onChange({ ...def, height: +e.target.value || 5 })} />
      </div>
    </div>
  );
}

export default function RiserEditor({ config, color, onConfigChange, onClose }: RiserEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RiserConfig>(config ?? defaultRisers());

  useEffect(() => {
    if (config) setDraft(cloneRisers(config));
  }, [config]);

  const update = (patch: Partial<RiserConfig>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    onConfigChange(next);
  };

  const items = riserItems(draft);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal stageplot-modal" role="dialog" aria-label={t('stagePlotEditor.riserError')} onClick={(e) => e.stopPropagation()}>
        <div className="stageplot-modal-header">
          <h3>{t('stagePlotEditor.riserTitle')}</h3>
          <div className="modal-header-actions">
            <button className="btn btn-primary" onClick={onClose}>{t('stagePlotEditor.done')}</button>
            <button className="btn-icon" onClick={onClose} aria-label={t('stagePlotEditor.close')}>✕</button>
          </div>
        </div>

        <div className="dk-preview">
          {(() => {
            const vb = riserViewBox(draft);
            return <RiserGraphic config={draft} color={color} boxW={vb.w} boxH={vb.h} />;
          })()}
          <div className="dk-summary">{riserSummary(draft)}</div>
        </div>

        <section className="dk-section">
          <h4>{t('stagePlotEditor.onWheels')}</h4>
          <label className="dk-checkbox">
            <input
              type="checkbox"
              checked={draft.onWheels}
              onChange={(e) => update({ onWheels: e.target.checked })}
            />
            <span>{t('stagePlotEditor.casterMounted')}</span>
          </label>
        </section>

        <section className="dk-section">
          <h4>{t('stagePlotEditor.count')}</h4>
          <div className="dk-steppers">
            <button className="btn" onClick={() => update({ count: Math.max(1, draft.count - 1) })}>{t('stagePlotEditor.minusRiser')}</button>
            <span>{draft.count}</span>
            <button className="btn" onClick={() => update({ count: Math.min(12, draft.count + 1) })}>{t('stagePlotEditor.plusRiser')}</button>
          </div>
        </section>

        <section className="dk-section">
          <h4>{t('stagePlotEditor.sizeMode')}</h4>
          <button
            className={`btn ${draft.uniform ? 'btn-primary' : ''}`}
            onClick={() => update({ uniform: true })}
          >
            {t('stagePlotEditor.uniform')}
          </button>
          <button
            className={`btn ${!draft.uniform ? 'btn-primary' : ''}`}
            onClick={() => update({ uniform: false })}
          >
            {t('stagePlotEditor.perRiser')}
          </button>
        </section>

        <section className="dk-section">
          <h4>{draft.uniform ? t('stagePlotEditor.sharedDimensions') : t('stagePlotEditor.perRiserDimensions')}</h4>
          {draft.uniform ? (
            <RiserRow def={draft.size} onChange={(d) => update({ size: d })} />
          ) : (
            <div className="dk-riser-list">
              {items.map((it, i) => (
                <div key={i} className="dk-riser-item">
                  <span className="dk-riser-idx">#{i + 1}</span>
                  <RiserRow
                    def={it}
                    onChange={(d) => {
                      const nextItems = items.map((x, j) => (j === i ? d : x));
                      update({ items: nextItems });
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
